import React, { useEffect, useRef, useState } from "react";
import CautionDangerIcon from "../Icons/CautionDangerIcon";
import InfoIcon from "../Icons/InfoIcon";
import clsx from "clsx";

interface InputProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly id: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly error?: string;
  readonly value?: string;
  readonly onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readonly onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  readonly onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  readonly onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly name?: string;
  readonly className?: string;
  readonly icon?: React.ReactNode;
  readonly containerClassName?: string;
  readonly labelClassName?: string;
  readonly textAreaClassName?: string;
  readonly inputRef?: React.RefObject<HTMLTextAreaElement>;
  readonly activeColor?: string;
  readonly innerContainerClassName?: string;
  readonly helperText?: string;
  readonly noBorder?: boolean;
  readonly minRow?: number;
  readonly endIcon?: React.ReactNode;
  readonly maxLength?: number;
}

const Index: React.FC<InputProps> = ({
  id,
  label,
  placeholder,
  error,
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  name,
  icon,
  labelClassName,
  containerClassName,
  textAreaClassName,
  className,
  inputRef,
  activeColor = "#253858",
  autoFocus = false,
  innerContainerClassName,
  helperText,
  noBorder = false,
  maxLength,
  endIcon,
  minRow,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const localRef = useRef<HTMLTextAreaElement>(null);

  const clonedIcon =
    icon &&
    React.cloneElement(icon as React.ReactElement, {
      color: error ? "#EB5757" : isFocused ? activeColor : "#BDC0C3",
    });

  const autoGrow = () => {
    const el = inputRef?.current || localRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    autoGrow();
    if (maxLength && e.target.value.length <= maxLength) {
      onChange?.(e);
    } else if (!maxLength) {
      onChange?.(e);
    }
  };

  useEffect(() => {
    autoGrow();
  }, [value]);
  return (
    <div className={`relative ${containerClassName}`}>
      <div
        className={`flex flex-col gap-[6px] group ${innerContainerClassName}`}
      >
        {label && (
          <label
            htmlFor={id}
            className={`text-xs leading-[18px] text-[#79808A] ${labelClassName}`}
          >
            {label}
          </label>
        )}
        <div
          className={`flex items-center gap-[8px] h-[48px] flex-row ${
            noBorder ? "" : "border rounded-[4px]"
          } py-[10px] px-[10px] ${textAreaClassName} ${
            noBorder
              ? ""
              : isFocused
                ? "!border-[#79808A] !border-[1px]"
                : "border-[#E9ECF2]"
          } ${error && "border-danger"}`}
        >
          {icon && <div>{clonedIcon}</div>}
          <textarea
            id={id}
            className={clsx(
              "text-sm rounded w-full text-gray-700 leading-[21px] focus:outline-none placeholder:text-[#BDC0C3] placeholder:font-normal placeholder:text-sm placeholder:leading-[21px] resize-none",
              className,
            )}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur?.(e);
            }}
            onFocus={(e) => {
              setIsFocused(true);
              autoGrow(); // Optional: grow on focus
              onFocus?.(e);
            }}
            onKeyDown={onKeyDown}
            name={name}
            ref={inputRef || localRef}
            autoFocus={autoFocus}
            rows={minRow ? minRow : 1}
          />
          {endIcon && <div>{endIcon}</div>}
        </div>
      </div>
      {helperText && !error && (
        <p className="text-[10px] leading-[14px] text-secondary absolute bottom-[-20px] flex items-center gap-[4px]">
          <InfoIcon size="16" />
          <span className="mt-[2px]">{helperText}</span>
        </p>
      )}
      {error && (
        <p className="text-[11px] leading-[18px] text-danger absolute bottom-[-18px] flex items-center gap-[4px]">
          <CautionDangerIcon />
          <span className="mt-[2px]">{error}</span>
        </p>
      )}
      {value && maxLength && (
        <p className="flex justify-end text-[11px] leading-[18px] text-gray-500 text-right">
          {value.length}/{maxLength} characters
        </p>
      )}
    </div>
  );
};

export default Index;
